/**
 * Submit hook for `GeneralInteractionForm`. Thin wrapper around the shared
 * `useEncounterUpsertSubmit` helper with the `general-interaction`
 * discriminator.
 *
 * @agentic connections
 */

import type { Encounter, Patient, PractitionerRole } from "@medplum/fhirtypes";
import type {
  EncounterActivityContext,
  GeneralInteractionFormData,
} from "@benchmark/core/types";

import { useEncounterUpsertSubmit } from "./useEncounterUpsertSubmit";

/**
 * Options for {@link useGeneralInteractionSubmit}.
 *
 * @agentic connections
 */
export type UseGeneralInteractionSubmitOptions = {
  patient: Patient | undefined;
  existingEncounter?: Encounter;
  practitionerRoles?: PractitionerRole[];
  activityContext?: EncounterActivityContext;
  onSuccess?: (encounterId: string) => void;
  onSubmitted?: () => void;
  getCareTimeMinutes?: () => number | undefined;
  getCareTimeCommunicationId?: () => string | undefined;
  getCareTimeEffectivePeriod?: () => { start: string; end: string } | undefined;
};

/**
 * Returns a submit handler for `GeneralInteractionForm`.
 *
 * @agentic connections
 */
export const useGeneralInteractionSubmit = (
  options: UseGeneralInteractionSubmitOptions,
): ((formData: GeneralInteractionFormData) => Promise<void>) =>
  useEncounterUpsertSubmit<"general-interaction">({
    type: "general-interaction",
    careTimeMethod: "general-interaction",
    ...options,
  });
